import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, switchMap, tap, map } from 'rxjs';
import { API_URL, SLACK_REDIRECT_URI } from '../constants';
import { Bot, CreateBotResponse } from '../interfaces/bot.interface';

export interface BrnServiceRequest {
  category: string;
  data: {
    shop: string;
    active: boolean;
    access_token: string;
  };
}

export interface BrnServiceResponse {
  status: boolean;
  service: {
    id: string;
    bot_id: string;
    name: string;
    category: string;
    active: boolean;
    data: {
      shop: string;
      active: boolean;
      code: string;
      access_token: string;
      synchronization: boolean;
    };
    fees: any[];
    capabilities: any[];
  };
}

export interface BrnServiceGetResponse {
  status: boolean;
  service: {
    id: string;
    bot_id: string;
    name: string;
    category: string;
    active: boolean;
    data: {
      synchronization: boolean;
      access_token: string;
      code: string;
      active: boolean;
      shop: string;
      connection: boolean;
      apps: any[];
    };
    capabilities: any[];
  };
}

export interface BrnOpenAIServiceResponse {
  status: boolean;
  service: {
    id: string;
    bot_id: string;
    name: string;
    category: string;
    active: boolean;
    data: {
      api_key: string;
    };
    capabilities: any[];
  };
}

export interface BrnAnthropicServiceResponse {
  status: boolean;
  service: {
    id: string;
    bot_id: string;
    name: string;
    category: string;
    active: boolean;
    data: {
      api_key: string;
    };
    capabilities: any[];
  };
}

export interface BrnKlaviyoServiceResponse {
  status: boolean;
  service: {
    id: string;
    bot_id: string;
    name: string;
    category: string;
    active: boolean;
    data: {
      api_key: string;
    };
    capabilities: any[];
  };
}

export interface BrnKlaviyoServiceRequest {
  category: string;
  data: {
    api_key: string;
  };
}

export interface BrnFacebookServiceResponse {
  status: boolean;
  service: {
    id: string;
    bot_id: string;
    name: string;
    category: string;
    active: boolean;
    data: {
      access_token: string;
      ad_account_id: string;
      ad_account_name: string;
    };
    capabilities: any[];
  };
}

export interface BrnFacebookServiceRequest {
  category: string;
  data: {
    access_token: string;
    ad_account_id: string;
    ad_account_name: string;
  };
}

export interface BrnOpenAIServiceRequest {
  category: string;
  data: {
    api_key: string;
  };
}

export interface BrnAnthropicServiceRequest {
  category: string;
  data: {
    api_key: string;
  };
}

export interface BrnOpenAIUpdateRequest {
  data: {
    api_key: string;
  };
}

export interface BrnAnthropicUpdateRequest {
  data: {
    api_key: string;
  };
}

export interface BrnDeleteResponse {
  status: boolean;
  message?: string;
}

export interface BrnServiceUpdateRequest {
  data: {
    shop?: string;
    active?: boolean;
    access_token?: string;
  };
}

export interface BrnToolRequest {
  properties: {
    active: boolean;
    ecommerce_platform: string | null;
  };
}

export interface BrnToolResponse {
  status: boolean;
}

export interface BrnToolGetResponse {
  status: boolean;
  tool: {
    bot_id: string;
    name: string;
    account_id: string;
    active: boolean;
    portal_id: string;
  };
}

export interface BrnToolUpdateRequest {
  properties: {
    ecommerce_platform?: string | null;
    active?: boolean;
    client_id?: string | null;
    channels?: {
      slack?: {
        client_id?: string | null;
      };
    };
  };
}

export interface BrnToolUpdateRequestData {
  ecommerce_platform?: string | null;
  active?: boolean;
  client_id?: string | null;
  channels?: {
    slack?: {
      client_id?: string | null;
    };
  };
}

export interface BrnToolUpdateResponse {
  status: boolean;
}

export interface BrnSlackConnectResponse {
  status: boolean;
  id: string;
}

export interface BrnSlackServiceRequest {
  // Add any required fields for Slack service creation if needed
}

export interface BrnSlackServiceResponse {
  status: boolean;
  service: {
    id: string;
    bot_id: string;
    name: string;
    category: string;
    active: boolean;
    fees: any;
    capabilities: any[];
  };
}

export interface BrnSlackAuthorizeRequest {
  code: string;
  redirect_uri: string;
}

export interface BrnSlackAuthorizeResponse {
  status: boolean;
  client?: {
    client_id: string;
    bot_id: string;
    portal_id: string;
    account_id: string;
    provider: string;
    name: string;
  };
}

export interface BrnClientResponse {
  status: boolean;
  client_id: string;
  bot_id: string;
  portal_id: string;
  account_id: string;
  provider: string;
  name: string;
}

export interface BrnDeleteClientResponse {
  status: boolean;
  message?: string;
}

export interface BrnUpdateClientRequest {
  bot_id: string;
  name: string;
}

export interface BrnUpdateClientResponse {
  status: boolean;
  client?: BrnClientResponse;
}

export interface BrnBotSettingsRequest {
  messaging: {
    agent: {
      provider: string | null;
    };
  };
}

export interface BrnBotSettingsResponse {
  status: boolean;
  settings?: {
    messaging: {
      agent: {
        provider: string | null;
      };
    };
  };
}

export interface BrnGoogleAdsInitRequest {
  redirect_url: string;
}

export interface BrnGoogleAdsInitResponse {
  status: boolean;
  url: string;
}

export interface BrnGoogleAdsServiceResponse {
  status: boolean;
  service: {
    id: string;
    bot_id: string;
    name: string;
    category: string;
    active: boolean;
    data: {
      access_token?: string;
      customer_id?: string;
      refresh_token?: string;
      ad_account_id?: string;
      ad_account_name?: string;
    };
    capabilities: any[];
  };
}

export interface BrnGoogleAdsServiceRequest {
  data: {
    active: boolean;
    customer_id?: string;
    auth: {
      redirect_url: string;
      code: string;
    };
  };
}

export interface GoogleAdsCustomer {
  ad_manager_account_id: string | null;
  ad_account_id: string;
  descriptiveName: string;
  manager: boolean;
  level: string;
  status: string;
}

export interface BrnGoogleAdsCustomersResponse {
  status: boolean;
  customers: GoogleAdsCustomer[];
}

export interface BrnGoogleAdsUpdateRequest {
  data: {
    ad_account_id: string;
  };
}

export interface BrnPlanRequest {
  budget: {
    total: number;
    channels: {
      'meta-ads'?: number;
      'google-ads'?: number;
    };
  };
  revenue: {
    total: number;
  };
}

export interface BrnPlanResponse {
  status: boolean;
  plan: {
    id: string;
    bot_id: string;
    date: string;
    budget: {
      channels: {
        'meta-ads'?: number;
        'google-ads'?: number;
      };
      total: number;
    };
    revenue: {
      total: number;
    };
  };
}

// Import task (ecommerce-insight)
export interface BrnImportTaskRequest {
  source: string;
  range: {
    since: string; // YYYY-MM-DD
    until: string; // YYYY-MM-DD
  };
}

export interface BrnImportTaskResponse {
  status: boolean;
}

// Schedule report (ecommerce-insight)
export interface BrnScheduleReportRequest {
  options: Record<string, unknown>;
  schedule: {
    frequency: 'daily';
    time: string; // HH:mm
  };
  output: {
    formatter: 'image';
  };
}

export interface BrnScheduleReportResponse {
  status: boolean;
}

export interface BrnScheduledReportsResponse {
  status: boolean;
  reports: any[];
}

// Widget/chat interfaces
export interface BrnWidgetConnection {
  provider_data: Record<string, unknown>;
}

export interface BrnWidgetConnectionsResponse {
  status: boolean;
  connections: Array<{
    id: string;
    provider: string;
    provider_data: Record<string, unknown>;
  }>;
}

export interface BrnWidgetCreateConnectionRequest {
  provider_data: Record<string, unknown>;
}

export interface BrnWidgetConnectionResponse {
  status: boolean;
  connection: {
    id: string;
    provider: string;
    provider_data: Record<string, unknown>;
  };
}

export interface BrnWidgetCreateClientRequest {
  name: string;
  bot_id: string;
  provider_data: Record<string, unknown>;
}

export interface BrnWidgetClient {
  client_id: string;
  name: string;
  provider: string;
  provider_data: Record<string, unknown>;
}

export interface BrnWidgetClientWithMeta extends BrnWidgetClient {
  bot_id: string;
  metadata?: {
    last_message_at?: string;
    last_message_preview?: string;
  };
}

export interface BrnWidgetCreateClientResponse {
  status: boolean;
  client: BrnWidgetClient;
}

export interface BrnWidgetSendMessageRequest {
  bot_id: string;
  user_id: string;
  type: 'text';
  content: {
    text: string;
  };
}

export interface BrnWidgetSendMessageResponse {
  status: boolean;
}

export interface BrnWidgetMessage {
  message: {
    type: string;
    content: Record<string, unknown>;
  };
  author: 'client' | 'bot';
  created: string;
}

export interface BrnWidgetMessageHistoryResponse {
  status: boolean;
  client: BrnWidgetClient;
  messages: BrnWidgetMessage[];
}

// Services catalog (by capability)
export interface BrnServicesByCapabilityResponse {
  status: boolean;
  services: Array<{
    name: string;
    category: string;
    capabilities: string[];
    fees: any[];
    installed: boolean;
  }>;
}

export interface BrnServiceResponseGenerationRequest {
  input: string;
  options: {
    system_prompt: string;
  };
}

export interface BrnServiceResponseGenerationResponse {
  status: boolean;
  // Flexible payload depending on provider
  response?: unknown;
  text?: string;
  output?: string;
  message?: string;
}


// Scenario types
export interface BrnScenarioRequest {
  bot_id: string;
  name: string;
  description: string;
  template: string;
  validation: {
    forced: boolean;
    status: string;
  };
  version: number;
  steps: Array<{
    id: string;
    name: string;
    component: string;
    properties: any;
    action: { next: string | null };
    position: { column: number; group: number };
  }>;
  keywords: string[];
}

export interface BrnScenarioResponse {
  status: boolean;
  scenario?: any;
}

@Injectable({
  providedIn: 'root'
})
export class BrnService {
  constructor(private http: HttpClient) { }

  create(botId: string, serviceData: BrnServiceRequest): Observable<BrnServiceResponse> {
    return this.http.post<BrnServiceResponse>(
      `${API_URL}/service/${botId}/shopify`,
      serviceData
    );
  }

  getService(botId: string): Observable<BrnServiceGetResponse> {
    return this.http.get<BrnServiceGetResponse>(
      `${API_URL}/service/${botId}/shopify`
    );
  }

  updateService(botId: string, serviceData: BrnServiceUpdateRequest): Observable<BrnServiceResponse> {
    return this.http.put<BrnServiceResponse>(
      `${API_URL}/service/${botId}/shopify`,
      serviceData
    );
  }

  deleteService(botId: string): Observable<BrnDeleteResponse> {
    return this.http.delete<BrnDeleteResponse>(
      `${API_URL}/service/${botId}/shopify`
    );
  }

  getBot(botId: string): Observable<Bot> {
    return this.http.get<Bot>(`${API_URL}/bot/${botId}`);
  }

  createBot(name: string): Observable<CreateBotResponse> {
    return this.http.post<CreateBotResponse>(`${API_URL}/bot`, { name });
  }

  createTool(botId: string): Observable<BrnToolResponse> {
    const toolData: BrnToolRequest = {
      properties: {
        active: true,
        ecommerce_platform: null,
      }
    };

    return this.http.post<BrnToolResponse>(
      `${API_URL}/tool/${botId}/ecommerce-insight`,
      toolData
    );
  }

  getTool(botId: string): Observable<BrnToolGetResponse> {
    return this.http.get<BrnToolGetResponse>(
      `${API_URL}/tool/${botId}/ecommerce-insight`
    );
  }

  updateTool(botId: string, properties: BrnToolUpdateRequestData): Observable<BrnToolUpdateResponse> {
    const updateData: BrnToolUpdateRequest = {
      properties
    };

    return this.http.patch<BrnToolUpdateResponse>(
      `${API_URL}/tool/${botId}/ecommerce-insight`,
      updateData
    );
  }

  connectSlack(botId: string): Observable<BrnSlackConnectResponse> {
    return this.http.post<BrnSlackConnectResponse>(
      `${API_URL}/connection/slack/${botId}`,
      {}
    );
  }

  createSlackService(botId: string): Observable<BrnSlackServiceResponse> {
    return this.http.post<BrnSlackServiceResponse>(
      `${API_URL}/service/${botId}/slack`,
      {}
    );
  }

  authorizeSlackService(botId: string, code: string): Observable<BrnSlackAuthorizeResponse> {
    const authorizeData: BrnSlackAuthorizeRequest = {
      code: code,
      redirect_uri: SLACK_REDIRECT_URI,
    };

    return this.http.post<BrnSlackAuthorizeResponse>(
      `${API_URL}/service/${botId}/slack/authorize`,
      authorizeData
    );
  }

  getClient(clientId: string): Observable<BrnClientResponse> {
    return this.http.get<BrnClientResponse>(
      `${API_URL}/client/${clientId}`
    );
  }

  deleteClient(clientId: string): Observable<BrnDeleteClientResponse> {
    return this.http.delete<BrnDeleteClientResponse>(
      `${API_URL}/client/${clientId}`
    );
  }

  updateClient(clientId: string, botId: string, name: string): Observable<BrnUpdateClientResponse> {
    const body: BrnUpdateClientRequest = {
      bot_id: botId,
      name
    };
    return this.http.put<BrnUpdateClientResponse>(
      `${API_URL}/client/${clientId}`,
      body
    );
  }

  getOpenAIService(botId: string): Observable<BrnOpenAIServiceResponse> {
    return this.http.get<BrnOpenAIServiceResponse>(
      `${API_URL}/service/${botId}/openai`
    );
  }

  createOpenAIService(botId: string, apiKey: string): Observable<BrnOpenAIServiceResponse> {
    const serviceData: BrnOpenAIServiceRequest = {
      category: 'ai',
      data: {
        api_key: apiKey
      }
    };

    return this.http.post<BrnOpenAIServiceResponse>(
      `${API_URL}/service/${botId}/openai`,
      serviceData
    ).pipe(
      switchMap(response => {
        if (response.status) {
          // If service creation successful, enable the agent
          return this.updateBotAgentProvider(botId, 'openai').pipe(
            // Return original service response regardless of agent update result
            tap({
              error: (error) => console.error('Failed to enable OpenAI agent:', error)
            }),
            // Always return the original service response
            switchMap(() => [response])
          );
        }
        return [response];
      })
    );
  }

  updateOpenAIService(botId: string, apiKey: string | null): Observable<BrnOpenAIServiceResponse | BrnDeleteResponse> {
    if (!apiKey?.trim()) {
      return this.deleteOpenAIService(botId);
    }

    const updateData: BrnOpenAIUpdateRequest = {
      data: {
        api_key: apiKey
      }
    };

    return this.http.put<BrnOpenAIServiceResponse>(
      `${API_URL}/service/${botId}/openai`,
      updateData
    ).pipe(
      switchMap(response => {
        if (response.status) {
          // If service update successful, enable the agent
          return this.updateBotAgentProvider(botId, 'openai').pipe(
            // Return original service response regardless of agent update result
            tap({
              error: (error) => console.error('Failed to enable OpenAI agent:', error)
            }),
            // Always return the original service response
            switchMap(() => [response])
          );
        }
        return [response];
      })
    );
  }

  getAnthropicService(botId: string): Observable<BrnAnthropicServiceResponse> {
    return this.http.get<BrnAnthropicServiceResponse>(
      `${API_URL}/service/${botId}/anthropic`
    );
  }

  createAnthropicService(botId: string, apiKey: string): Observable<BrnAnthropicServiceResponse> {
    const serviceData: BrnAnthropicServiceRequest = {
      category: 'ai',
      data: {
        api_key: apiKey
      }
    };

    return this.http.post<BrnAnthropicServiceResponse>(
      `${API_URL}/service/${botId}/anthropic`,
      serviceData
    ).pipe(
      switchMap(response => {
        if (response.status) {
          // If service creation successful, enable the agent
          return this.updateBotAgentProvider(botId, 'anthropic').pipe(
            // Return original service response regardless of agent update result
            tap({
              error: (error) => console.error('Failed to enable Anthropic agent:', error)
            }),
            // Always return the original service response
            switchMap(() => [response])
          );
        }
        return [response];
      })
    );
  }

  updateAnthropicService(botId: string, apiKey: string | null): Observable<BrnAnthropicServiceResponse | BrnDeleteResponse> {
    if (!apiKey?.trim()) {
      return this.deleteAnthropicService(botId);
    }

    const updateData: BrnAnthropicUpdateRequest = {
      data: {
        api_key: apiKey
      }
    };

    return this.http.put<BrnAnthropicServiceResponse>(
      `${API_URL}/service/${botId}/anthropic`,
      updateData
    ).pipe(
      switchMap(response => {
        if (response.status) {
          // If service update successful, enable the agent
          return this.updateBotAgentProvider(botId, 'anthropic').pipe(
            // Return original service response regardless of agent update result
            tap({
              error: (error) => console.error('Failed to enable Anthropic agent:', error)
            }),
            // Always return the original service response
            switchMap(() => [response])
          );
        }
        return [response];
      })
    );
  }

  deleteAnthropicService(botId: string): Observable<BrnDeleteResponse> {
    return this.http.delete<BrnDeleteResponse>(
      `${API_URL}/service/${botId}/anthropic`
    );
  }

  getKlaviyoService(botId: string): Observable<BrnKlaviyoServiceResponse> {
    return this.http.get<BrnKlaviyoServiceResponse>(
      `${API_URL}/service/${botId}/klaviyo`
    );
  }

  createKlaviyoService(botId: string, apiKey: string): Observable<BrnKlaviyoServiceResponse> {
    const serviceData: BrnKlaviyoServiceRequest = {
      category: 'marketing',
      data: {
        api_key: apiKey
      }
    };

    return this.http.post<BrnKlaviyoServiceResponse>(
      `${API_URL}/service/${botId}/klaviyo`,
      serviceData
    );
  }

  updateKlaviyoService(botId: string, apiKey: string): Observable<BrnKlaviyoServiceResponse> {
    const updateData = {
      data: {
        api_key: apiKey
      }
    };

    return this.http.put<BrnKlaviyoServiceResponse>(
      `${API_URL}/service/${botId}/klaviyo`,
      updateData
    );
  }

  getFacebookService(botId: string): Observable<BrnFacebookServiceResponse> {
    return this.http.get<BrnFacebookServiceResponse>(
      `${API_URL}/service/${botId}/meta-ads`
    );
  }

  createFacebookService(botId: string, accessToken: string, adAccountId: string, adAccountName: string): Observable<BrnFacebookServiceResponse> {
    const serviceData: BrnFacebookServiceRequest = {
      category: 'marketing',
      data: {
        access_token: accessToken,
        ad_account_id: adAccountId,
        ad_account_name: adAccountName
      }
    };

    return this.http.post<BrnFacebookServiceResponse>(
      `${API_URL}/service/${botId}/meta-ads`,
      serviceData
    );
  }

  updateFacebookService(botId: string, accessToken: string, adAccountId: string, adAccountName: string): Observable<BrnFacebookServiceResponse> {
    const updateData = {
      data: {
        access_token: accessToken,
        ad_account_id: adAccountId,
        ad_account_name: adAccountName
      }
    };

    return this.http.put<BrnFacebookServiceResponse>(
      `${API_URL}/service/${botId}/meta-ads`,
      updateData
    );
  }

  deleteFacebookService(botId: string): Observable<BrnDeleteResponse> {
    return this.http.delete<BrnDeleteResponse>(
      `${API_URL}/service/${botId}/meta-ads`
    );
  }

  deleteOpenAIService(botId: string): Observable<BrnDeleteResponse> {
    return this.http.delete<BrnDeleteResponse>(
      `${API_URL}/service/${botId}/openai`
    ).pipe(
      switchMap(response => {
        if (response.status) {
          // If service deletion successful, disable the agent
          return this.updateBotAgentProvider(botId, null).pipe(
            // Return original delete response regardless of agent update result
            tap({
              error: (error) => console.error('Failed to disable OpenAI agent:', error)
            }),
            // Always return the original delete response
            switchMap(() => [response])
          );
        }
        return [response];
      })
    );
  }

  updateBotAgentProvider(botId: string, provider: string | null): Observable<BrnBotSettingsResponse> {
    const settings: BrnBotSettingsRequest = {
      messaging: {
        agent: {
          provider: provider // 'openai' to enable, null to disable
        }
      }
    };

    return this.http.post<BrnBotSettingsResponse>(
      `${API_URL}/bot/settings/${botId}`,
      settings
    );
  }

  initGoogleAdsAuth(redirectUrl: string): Observable<BrnGoogleAdsInitResponse> {
    const initData: BrnGoogleAdsInitRequest = {
      redirect_url: redirectUrl
    };

    return this.http.post<BrnGoogleAdsInitResponse>(
      `${API_URL}/service/google-ads/init/auth`,
      initData
    );
  }

  getGoogleAdsService(botId: string): Observable<BrnGoogleAdsServiceResponse> {
    return this.http.get<BrnGoogleAdsServiceResponse>(
      `${API_URL}/service/${botId}/google-ads`
    );
  }

  createGoogleAdsService(botId: string, code: string, redirectUrl: string): Observable<BrnGoogleAdsServiceResponse> {
    const serviceData: BrnGoogleAdsServiceRequest = {
      data: {
        active: true,
        auth: {
          redirect_url: redirectUrl,
          code: code
        }
      }
    };

    return this.http.post<BrnGoogleAdsServiceResponse>(
      `${API_URL}/service/${botId}/google-ads`,
      serviceData
    );
  }

  getGoogleAdsCustomers(botId: string): Observable<BrnGoogleAdsCustomersResponse> {
    return this.http.get<BrnGoogleAdsCustomersResponse>(
      `${API_URL}/service/${botId}/google-ads/customers`
    );
  }

  updateGoogleAdsService(botId: string, adAccountId: string): Observable<BrnGoogleAdsServiceResponse> {
    const updateData: BrnGoogleAdsUpdateRequest = {
      data: {
        ad_account_id: adAccountId
      }
    };

    return this.http.put<BrnGoogleAdsServiceResponse>(
      `${API_URL}/service/${botId}/google-ads`,
      updateData
    );
  }

  deleteGoogleAdsService(botId: string): Observable<BrnDeleteResponse> {
    return this.http.delete<BrnDeleteResponse>(
      `${API_URL}/service/${botId}/google-ads`
    );
  }

  getPlan(botId: string, date: string): Observable<BrnPlanResponse> {
    return this.http.get<BrnPlanResponse>(
      `${API_URL}/tool/${botId}/ecommerce-insight/plan/${date}`
    );
  }

  setPlan(botId: string, date: string, planData: BrnPlanRequest): Observable<BrnPlanResponse> {
    return this.http.post<BrnPlanResponse>(
      `${API_URL}/tool/${botId}/ecommerce-insight/plan/${date}`,
      planData
    );
  }

  // Create import task for ecommerce-insight tool
  createImportTask(
    botId: string,
    source: string,
    since: string,
    until: string
  ): Observable<BrnImportTaskResponse> {
    const payload: BrnImportTaskRequest = {
      source,
      range: { since, until }
    };

    return this.http.post<BrnImportTaskResponse>(
      `${API_URL}/tool/${botId}/ecommerce-insight/import/task`,
      payload
    );
  }

  // Schedule a daily report at the current UTC HH:mm unless a time is provided
  scheduleReport(
    botId: string,
    report: string,
    time?: string
  ): Observable<BrnScheduleReportResponse> {
    const timeHHmm = time ?? this.getCurrentUtcTimeHHmm();
    const payload: BrnScheduleReportRequest = {
      options: {},
      schedule: {
        frequency: 'daily',
        time: timeHHmm
      },
      output: {
        formatter: 'image'
      }
    };

    return this.http.post<BrnScheduleReportResponse>(
      `${API_URL}/tool/${botId}/ecommerce-insight/schedule/report/${report}`,
      payload
    );
  }

  getScheduledReports(botId: string): Observable<BrnScheduledReportsResponse> {
    return this.http.get<BrnScheduledReportsResponse>(
      `${API_URL}/tool/${botId}/ecommerce-insight/schedule/reports`
    );
  }

  isReportScheduled(botId: string, report: string): Observable<boolean> {
    const reportSlug = report.toLowerCase();
    return this.getScheduledReports(botId).pipe(
      map((resp: any) => {
        const list = Array.isArray(resp?.reports)
          ? resp.reports
          : Array.isArray(resp)
          ? resp
          : [];
        return list.some((r: any) => {
          const candidates: Array<string | undefined> = [
            r?.report,
            r?.name,
            r?.slug,
            r?.type,
            r?.id,
            r?.path,
            r?.url,
            r?.key
          ];
          return candidates.some(
            v => typeof v === 'string' && v.toLowerCase().includes(reportSlug)
          );
        });
      })
    );
  }

  scheduleReportIfMissing(botId: string, report: string): Observable<boolean> {
    return this.isReportScheduled(botId, report).pipe(
      switchMap(exists => {
        if (exists) {
          return [true];
        }
        return this.scheduleReport(botId, report).pipe(map(r => !!r?.status));
      })
    );
  }

  private getCurrentUtcTimeHHmm(): string {
    const now = new Date();
    const hh = String(now.getUTCHours()).padStart(2, '0');
    const mm = String(now.getUTCMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  // Create default "Report" scenario after bot creation
  createScenario(botId: string): Observable<boolean> {
    const payload: BrnScenarioRequest = {
      bot_id: botId,
      name: 'Report',
      description: 'Report',
      template: 'visual',
      validation: {
        forced: true,
        status: 'ok'
      },
      version: 1,
      steps: [
        {
          id: 'STEP01',
          name: 'Type',
          component: 'pattern',
          properties: {
            regex: '/\\/report (.*)/',
            save: ['scenario.report_type']
          },
          action: {
            next: 'STEP02'
          },
          position: {
            column: 0,
            group: 0
          }
        },
        {
          id: 'STEP02',
          name: 'Report',
          component: 'ecommerce-insight.report',
          properties: {
            type: 'scenario.report_type'
          },
          action: {
            next: null
          },
          position: {
            column: 1,
            group: 0
          }
        }
      ],
      keywords: ['/report *']
    };
    return this.http
      .post(`${API_URL}/scenario`, payload, { observe: 'response' })
      .pipe(map(response => response.ok));
  }

  // Widget/chat APIs
  getWidgetConnections(botId: string): Observable<BrnWidgetConnectionsResponse> {
    return this.http.get<BrnWidgetConnectionsResponse>(
      `${API_URL}/connections/${botId}`
    );
  }

  createWidgetConnection(botId: string, providerData: BrnWidgetConnection['provider_data']): Observable<BrnWidgetConnectionResponse> {
    const body: BrnWidgetCreateConnectionRequest = { provider_data: providerData };
    return this.http.post<BrnWidgetConnectionResponse>(
      `${API_URL}/connection/widget/${botId}`,
      body
    );
  }

  createWidgetClient(request: BrnWidgetCreateClientRequest): Observable<BrnWidgetCreateClientResponse> {
    const body = {
      name: request.name,
      provider: 'widget',
      bot_id: request.bot_id,
      provider_data: request.provider_data
    };

    console.log('[API] createWidgetClient', body);
    return this.http.post<BrnWidgetCreateClientResponse>(
      `${API_URL}/client`,
      body
    );
  }

  sendWidgetMessage(payload: BrnWidgetSendMessageRequest): Observable<BrnWidgetSendMessageResponse> {
    console.log('[API] sendWidgetMessage request', payload);
    return this.http.post<BrnWidgetSendMessageResponse>(
      `${API_URL}/message/receive/widget`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    ).pipe(
      tap(response => console.log('[API] sendWidgetMessage response', response))
    );
  }

  getWidgetMessageHistory(botId: string, userId: string): Observable<BrnWidgetMessageHistoryResponse> {
    console.log('[API] getWidgetMessageHistory', { botId, userId });
    return this.http.get<BrnWidgetMessageHistoryResponse>(
      `${API_URL}/widget/messages/${botId}/${userId}`
    );
  }

  getWidgetClients(botId: string, name?: string): Observable<BrnWidgetClientWithMeta[]> {
    let params = new HttpParams().set('provider', 'widget');
    if (name && name.trim().length > 0) {
      params = params.set('name', name.trim());
    }
    console.log('[API] getWidgetClients', { botId, name });
    return this.http.get<{ status: boolean; clients: BrnWidgetClientWithMeta[] }>(
      `${API_URL}/clients/${botId}`,
      { params }
    ).pipe(map(resp => resp?.clients ?? []));
  }

  // Services catalog
  getServicesByCapability(botId: string, capability: string): Observable<BrnServicesByCapabilityResponse> {
    const params = new HttpParams()
      .set('bot_id', botId)
      .set('capability', capability);
    console.log('[API] getServicesByCapability', { botId, capability });
    return this.http.get<BrnServicesByCapabilityResponse>(
      `${API_URL}/services`,
      { params }
    );
  }

  generateServiceResponse(
    botId: string,
    serviceName: string,
    input: string,
    systemPrompt: string = "based on my message come up with short conversation name, reply only with the conversation name, don't quote it"
  ): Observable<BrnServiceResponseGenerationResponse> {
    const payload: BrnServiceResponseGenerationRequest = {
      input,
      options: {
        system_prompt: systemPrompt
      }
    };
    console.log('[API] generateServiceResponse', { botId, serviceName, input, systemPrompt });
    return this.http.post<BrnServiceResponseGenerationResponse>(
      `${API_URL}/service/${botId}/${serviceName}/response`,
      payload
    );
  }
} 